import { type ReactNode, useMemo, useState } from "react";
import { Icon, StatusProxy } from "@focus/focusUI";
import { useCdmData, useCdmInfo } from "../services/cdm";
import type { CDMInfo as CdmFileInfo } from "../services/cdm";
import type { CDMInfo as ConjunctionCdmInfo } from "../services/conjunctions";

import { Conjunction, Visual3DWidget } from "@focus/visual3D";
import { XMLViewer } from "../components/XMLViewer";
import { combineErrors, combineStatuses } from "../utils";
import { saveAs } from "file-saver";

/** Component that fetches the information required to show a CDM and renders a children with it. Used to abstract  */
export const CDMContainer = ({
  cdm,
  fallback,
}: {
  cdm?: ConjunctionCdmInfo | null;
  fallback?: ReactNode;
}) => {
  if (!cdm?.id) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm font-medium text-slate-500">
        No CDM available for this conjunction.
      </div>
    );
  }

  return <CDMViewer cdmId={cdm.id} />;
};

const CDMViewer = ({ cdmId }: { cdmId: string }) => {
  const [show3D, setShow3D] = useState(true);

  const cdmData = useCdmData(cdmId);
  const cdmInfo = useCdmInfo(cdmId);

  const combinedStatus = combineStatuses([cdmData.status, cdmInfo.status]);

  const combinedError = combineErrors([cdmData.error, cdmInfo.error]);

  const xml = useMemo(() => cdmInfo?.value?.fileContent || "", [cdmInfo]);

  const handleDownload = (obj: CdmFileInfo) => {
    const { fileContent, filename } = obj;

    // Create XML blob file type
    const blob = new Blob([fileContent], {
      type: "application/xml;charset=utf-8;",
    });

    // Filename (need to add .xml)
    const fileName = filename?.toLowerCase().endsWith(".xml")
      ? filename
      : `${filename || "cdm"}.xml`;

    saveAs(blob, fileName);
  };

  const viewOptions = [
    { id: "xml", label: "XML" },
    { id: "3d", label: "3D" },
  ];

  return (
    <StatusProxy status={combinedStatus} error={combinedError}>
      <div className="mx-auto flex h-full min-h-0 w-full flex-col bg-white">
        <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-2">
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            <div className="inline-flex min-w-max items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
              {viewOptions.map((option) => {
                const isSelected =
                  (option.id === "3d" && show3D) ||
                  (option.id === "xml" && !show3D);

                return (
                  <button
                    className={`cursor-pointer rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                      isSelected
                        ? "focusoc-theme-surface shadow-sm"
                        : "text-slate-600 hover:bg-white hover:text-slate-950"
                    }`}
                    key={option.id}
                    onClick={() => setShow3D(option.id === "3d")}
                    type="button"
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            <button
              aria-label="Download CDM"
              className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-950 disabled:cursor-default disabled:opacity-50"
              disabled={!cdmInfo?.value}
              onClick={() => cdmInfo?.value && handleDownload(cdmInfo.value)}
              title="Download CDM"
              type="button"
            >
              <Icon icon="download" size={14} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {!show3D ? (
            <XMLViewer content={xml} />
          ) : (
            <div className="flex h-full w-full">
              <Visual3DWidget>
                {cdmData?.value?.map?.((e) => (
                  <Conjunction cdm={e} key={e.messageId} />
                ))}
              </Visual3DWidget>
            </div>
          )}
        </div>
      </div>
    </StatusProxy>
  );
};
